'use client';

import { useMemo, useState } from 'react';
import AppFooter from '@/components/AppFooter';
import AppLogo from '@/components/AppLogo';
import BottomNavigation from '@/components/BottomNavigation';
import CategoryEvolutionChart from '@/components/CategoryEvolutionChart';
import { CategoryIcon } from '@/components/CategoryIcon';
import DonutChart from '@/components/DonutChart';
import StatsCategoryChart from '@/components/StatsCategoryChart';
import { useAppSettings } from '@/hooks/useAppSettings';
import { useCategories } from '@/hooks/useCategories';
import { useRecurringExpenses } from '@/hooks/useRecurringExpenses';
import { useTags } from '@/hooks/useTags';
import { useTransactions } from '@/hooks/useTransactions';
import { TAG_COLORS, colorForCategory, colorForIndex } from '@/lib/colors';
import { formatMonthKey, formatMonthLabel } from '@/lib/date';
import { formatMoney } from '@/lib/money';

export default function StatsPage() {
  const currentMonth = formatMonthKey(new Date());
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const { transactions, loading, error: transactionsError } = useTransactions();
  const { categories, error: categoriesError } = useCategories();
  const { tags, error: tagsError } = useTags();
  const { settings, error: settingsError } = useAppSettings();
  const { recurringExpenses, error: recurringError } = useRecurringExpenses();
  const pageError = transactionsError || categoriesError || tagsError || settingsError || recurringError;
  const money = (value: number) => formatMoney(value, settings.currency);

  const monthOptions = useMemo(() => {
    const months = new Set([currentMonth]);
    transactions.forEach((tx) => months.add(tx.date.slice(0, 7)));
    return Array.from(months).sort((a, b) => b.localeCompare(a));
  }, [transactions, currentMonth]);

  const selectedMonthLabel = formatMonthLabel(selectedMonth);

  const monthlyExpenses = useMemo(
    () => transactions.filter((tx) => tx.type === 'expense' && tx.date.startsWith(selectedMonth)),
    [transactions, selectedMonth]
  );

  const monthlyTransactions = useMemo(
    () => transactions.filter((tx) => tx.date.startsWith(selectedMonth)),
    [transactions, selectedMonth]
  );

  const selectedStats = useMemo(() => {
    const income = monthlyTransactions.filter((tx) => tx.type === 'income').reduce((sum, tx) => sum + tx.amount, 0);
    const expense = monthlyTransactions.filter((tx) => tx.type === 'expense').reduce((sum, tx) => sum + tx.amount, 0);
    const categoryById = new Map(categories.map((category) => [category.id, category]));
    const categorySpending = categories.reduce<Record<string, number>>((acc, category) => {
      acc[category.id] = 0;
      return acc;
    }, {});
    let uncategorizedSpending = 0;

    monthlyTransactions.forEach((tx) => {
      if (tx.type !== 'expense') return;

      if (tx.categoryId && categoryById.has(tx.categoryId)) {
        categorySpending[tx.categoryId] = (categorySpending[tx.categoryId] ?? 0) + tx.amount;
      } else {
        uncategorizedSpending += tx.amount;
      }
    });

    const topCategories = [
      ...Object.entries(categorySpending).map(([id, amount]) => {
        const category = categoryById.get(id);
        return {
          id,
          name: category?.name ?? 'Sans catégorie',
          amount,
          icon: category?.icon,
          color: category?.color,
        };
      }),
      {
        id: 'uncategorized',
        name: 'Sans catégorie',
        amount: uncategorizedSpending,
        color: '#94a3b8',
      },
    ]
      .filter((item) => item.amount > 0)
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 5);

    const [year, monthNumber] = selectedMonth.split('-').map(Number);
    const totalDays = new Date(year, monthNumber, 0).getDate();

    return {
      income,
      expense,
      balance: income - expense,
      dailyAverage: expense / Math.max(totalDays, 1),
      totalDays,
      topCategories,
    };
  }, [categories, monthlyTransactions, selectedMonth]);

  const categorySegments = useMemo(() => {
    const categoryIds = new Set(categories.map((category) => category.id));
    const segments = categories.map((category, index) => ({
      id: category.id,
      label: category.name,
      value: monthlyExpenses
        .filter((tx) => tx.categoryId === category.id)
        .reduce((sum, tx) => sum + tx.amount, 0),
      color: colorForCategory(category, index, categories),
      icon: category.icon,
    }));
    const uncategorizedTotal = monthlyExpenses
      .filter((tx) => !tx.categoryId || !categoryIds.has(tx.categoryId))
      .reduce((sum, tx) => sum + tx.amount, 0);

    if (uncategorizedTotal > 0) {
      segments.push({
        id: 'uncategorized',
        label: 'Sans catégorie',
        value: uncategorizedTotal,
        color: '#94a3b8',
        icon: undefined,
      });
    }

    return segments.filter((segment) => segment.value > 0);
  }, [categories, monthlyExpenses]);

  const recurringSegments = useMemo(() => {
    const categoryIds = new Set(categories.map((category) => category.id));
    const activeRecurringExpenses = recurringExpenses.filter((item) => item.active);
    const segments = categories.map((category, index) => ({
      id: category.id,
      label: category.name,
      value: activeRecurringExpenses
        .filter((item) => item.categoryId === category.id)
        .reduce((sum, item) => sum + item.amount, 0),
      color: colorForCategory(category, index, categories),
      icon: category.icon,
    }));
    const uncategorizedTotal = activeRecurringExpenses
      .filter((item) => !item.categoryId || !categoryIds.has(item.categoryId))
      .reduce((sum, item) => sum + item.amount, 0);

    if (uncategorizedTotal > 0) {
      segments.push({
        id: 'uncategorized',
        label: 'Sans catégorie',
        value: uncategorizedTotal,
        color: '#94a3b8',
        icon: undefined,
      });
    }

    return segments.filter((segment) => segment.value > 0);
  }, [categories, recurringExpenses]);

  const recurringTotal = useMemo(
    () => recurringSegments.reduce((sum, segment) => sum + segment.value, 0),
    [recurringSegments]
  );

  const tagCharts = useMemo(
    () =>
      categories
        .map((category, categoryIndex) => {
          const categoryExpenses = monthlyExpenses.filter((tx) => tx.categoryId === category.id);
          const tagTotals = new Map<string, number>();
          let withoutTag = 0;

          categoryExpenses.forEach((tx) => {
            const txTagIds = tx.tagIds.filter((tagId) => tags.some((tag) => tag.id === tagId));

            if (txTagIds.length === 0) {
              withoutTag += tx.amount;
              return;
            }

            const amountPerTag = tx.amount / txTagIds.length;
            txTagIds.forEach((tagId) => {
              tagTotals.set(tagId, (tagTotals.get(tagId) ?? 0) + amountPerTag);
            });
          });

          const segments = Array.from(tagTotals.entries())
            .map(([tagId, amount], tagIndex) => ({
              id: tagId,
              label: tags.find((tag) => tag.id === tagId)?.name ?? 'Tag supprimé',
              value: amount,
              color: colorForIndex(categoryIndex + tagIndex, TAG_COLORS),
            }))
            .filter((segment) => segment.value > 0);

          if (withoutTag > 0) {
            segments.push({
              id: `${category.id}-without-tag`,
              label: 'Sans tag',
              value: withoutTag,
              color: '#94a3b8',
            });
          }

          return {
            id: category.id,
            name: category.name,
            total: categoryExpenses.reduce((sum, tx) => sum + tx.amount, 0),
            segments,
          };
        })
        .filter((chart) => chart.total > 0),
    [categories, monthlyExpenses, tags]
  );

  const transactionGroups = useMemo(() => {
    const categoryIds = new Set(categories.map((category) => category.id));
    const groups: Array<{
      id: string;
      name: string;
      transactions: typeof monthlyTransactions;
      income: number;
      expense: number;
      category: (typeof categories)[number] | null;
    }> = categories
      .map((category) => {
        const groupTransactions = monthlyTransactions.filter((tx) => tx.categoryId === category.id);
        return {
          id: category.id,
          name: category.name,
          transactions: groupTransactions,
          income: groupTransactions.filter((tx) => tx.type === 'income').reduce((sum, tx) => sum + tx.amount, 0),
          expense: groupTransactions.filter((tx) => tx.type === 'expense').reduce((sum, tx) => sum + tx.amount, 0),
          category,
        };
      })
      .filter((group) => group.transactions.length > 0);

    const uncategorizedTransactions = monthlyTransactions.filter(
      (tx) => !tx.categoryId || !categoryIds.has(tx.categoryId)
    );

    if (uncategorizedTransactions.length > 0) {
      groups.push({
        id: 'uncategorized',
        name: 'Sans catégorie',
        transactions: uncategorizedTransactions,
        income: uncategorizedTransactions.filter((tx) => tx.type === 'income').reduce((sum, tx) => sum + tx.amount, 0),
        expense: uncategorizedTransactions.filter((tx) => tx.type === 'expense').reduce((sum, tx) => sum + tx.amount, 0),
        category: null,
      });
    }

    return groups;
  }, [categories, monthlyTransactions]);

  const balanceTone = selectedStats.balance >= 0 ? 'text-emerald-200' : 'text-rose-200';

  return (
    <main className="page-shell">
      <div className="page-inner">
        {pageError ? (
          <div className="no-print rounded-2xl border border-rose-900/70 bg-rose-950 p-4 text-sm text-rose-200">
            {pageError}
          </div>
        ) : null}

        <section className="overflow-hidden rounded-2xl border border-brand-500/20 bg-slate-900 shadow-sm">
          <div className="no-print bg-brand-700 px-4 py-5 text-white min-[380px]:px-5">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div className="min-w-0">
                <AppLogo className="text-brand-100" markClassName="border-white/25" textClassName="text-brand-100" />
                <h1 className="mt-1 text-xl font-semibold leading-tight text-white min-[380px]:text-2xl">
                  Vue simple des dépenses
                </h1>
                <p className="mt-2 text-sm leading-6 text-brand-100">
                  Un résumé lisible du mois, sans table compliquée.
                </p>
              </div>
              <div className="grid gap-2 sm:w-56">
                <label>
                  <span className="sr-only">Mois affiché</span>
                  <select
                    value={selectedMonth}
                    onChange={(event) => setSelectedMonth(event.target.value)}
                    className="w-full border-brand-500/30 bg-slate-950/80 px-4 py-3 text-white"
                  >
                    {monthOptions.map((month) => (
                      <option key={month} value={month}>
                        {formatMonthLabel(month)}
                      </option>
                    ))}
                  </select>
                </label>
                <button type="button" onClick={() => window.print()} className="secondary-button w-full bg-slate-950/80 text-white">
                  Imprimer / PDF
                </button>
              </div>
            </div>
          </div>

          <div className="print-only p-5">
            <p className="text-sm font-medium text-slate-500">Licaio</p>
            <h1 className="mt-1 text-2xl font-semibold text-slate-950">Récapitulatif - {selectedMonthLabel}</h1>
          </div>

          <div className="grid gap-px bg-slate-800 sm:grid-cols-4">
            <div className="bg-slate-900 p-4">
              <p className="text-sm font-medium text-slate-400">Entrées</p>
              <p className="mt-2 truncate text-2xl font-semibold text-emerald-300">
                {loading ? '...' : money(selectedStats.income)}
              </p>
            </div>
            <div className="bg-slate-900 p-4">
              <p className="text-sm font-medium text-slate-400">Dépenses</p>
              <p className="mt-2 truncate text-2xl font-semibold text-rose-300">
                {loading ? '...' : money(selectedStats.expense)}
              </p>
            </div>
            <div className="bg-slate-900 p-4">
              <p className="text-sm font-medium text-slate-400">Solde</p>
              <p className={`mt-2 truncate text-2xl font-semibold ${balanceTone}`}>
                {loading ? '...' : money(selectedStats.balance)}
              </p>
            </div>
            <div className="bg-slate-900 p-4">
              <p className="text-sm font-medium text-slate-400">Moyenne / jour</p>
              <p className="mt-2 truncate text-2xl font-semibold text-slate-100">
                {loading ? '...' : money(selectedStats.dailyAverage)}
              </p>
            </div>
          </div>
        </section>

        <DonutChart
          title={`Dépenses - ${selectedMonthLabel}`}
          subtitle="Répartition par catégorie"
          segments={categorySegments}
          emptyLabel="Aucune dépense pour ce mois."
          centerLabel="Mois"
          centerValue={money(selectedStats.expense)}
        />

        <DonutChart
          title={`Achats récurrents - ${selectedMonthLabel}`}
          subtitle="Répartition par catégorie"
          segments={recurringSegments}
          emptyLabel="Aucun achat récurrent actif pour ce mois."
          centerLabel="Fixes"
          centerValue={money(recurringTotal)}
        />

        <CategoryEvolutionChart transactions={transactions} categories={categories} />

        <section className="section-card">
          <div className="mb-5 flex flex-col gap-3 min-[420px]:flex-row min-[420px]:items-end min-[420px]:justify-between">
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-slate-100">Lecture rapide</h2>
              <p className="mt-2 text-sm text-slate-400">Quelques repères pour comprendre le mois affiché.</p>
            </div>
            <span className="w-fit rounded-full bg-slate-800 px-3 py-1 text-xs font-semibold text-slate-300">
              {selectedStats.totalDays} jours
            </span>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-2xl bg-slate-950 p-4">
              <p className="text-sm font-medium text-slate-400">Transactions du mois</p>
              <p className="mt-2 text-2xl font-semibold text-slate-100">{monthlyTransactions.length}</p>
            </div>
            <div className="rounded-2xl bg-slate-950 p-4">
              <p className="text-sm font-medium text-slate-400">Catégories actives</p>
              <p className="mt-2 text-2xl font-semibold text-slate-100">{categorySegments.length}</p>
            </div>
          </div>
        </section>

        <section className="space-y-4">
          <div className="mb-5">
            <h2 className="text-lg font-semibold text-slate-100">Tags par catégorie</h2>
            <p className="mt-2 text-sm text-slate-400">Le détail utile quand une catégorie devient trop vague.</p>
          </div>
          <div className="grid gap-4">
            {tagCharts.length === 0 ? (
              <div className="card-muted text-sm">Aucune dépense avec tags pour ce mois.</div>
            ) : (
              tagCharts.map((chart) => (
                <DonutChart
                  key={chart.id}
                  title={chart.name}
                  subtitle="Répartition des tags"
                  segments={chart.segments}
                  emptyLabel="Aucun tag utilisé pour cette catégorie."
                  centerLabel="Total"
                  centerValue={money(chart.total)}
                />
              ))
            )}
          </div>
        </section>

        <section className="section-card no-print">
          <div className="mb-5">
            <h2 className="text-lg font-semibold text-slate-100">Détail des transactions</h2>
            <p className="mt-2 text-sm text-slate-400">{selectedMonthLabel}</p>
          </div>
          {transactionGroups.length === 0 ? (
            <div className="card-muted text-sm">Aucune transaction pour ce mois.</div>
          ) : (
            <div className="space-y-4">
              {transactionGroups.map((group) => (
                <div key={group.id} className="rounded-2xl border border-slate-800 bg-slate-950 p-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="flex min-w-0 items-center gap-3">
                      {group.category ? <CategoryIcon category={group.category} className="h-10 w-10" /> : null}
                      <div className="min-w-0">
                        <h3 className="truncate font-semibold text-slate-100">{group.name}</h3>
                        <p className="mt-1 text-sm text-slate-400">
                          {group.transactions.length} opération{group.transactions.length > 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm font-semibold sm:flex sm:flex-wrap">
                      {group.income > 0 ? (
                        <span className="rounded-full bg-emerald-500/10 px-3 py-1 text-emerald-300">
                          +{money(group.income)}
                        </span>
                      ) : null}
                      {group.expense > 0 ? (
                        <span className="rounded-full bg-rose-500/10 px-3 py-1 text-rose-300">
                          -{money(group.expense)}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <div className="mt-4 max-h-96 space-y-2 overflow-y-auto pr-1">
                    {group.transactions.map((tx) => {
                      const isIncome = tx.type === 'income';
                      const txTags = tx.tagIds
                        .map((tagId) => tags.find((tag) => tag.id === tagId))
                        .filter((tag): tag is NonNullable<typeof tag> => Boolean(tag));

                      return (
                        <div key={tx.id} className="rounded-2xl border border-slate-800 bg-slate-900 p-3">
                          <div className="flex flex-col gap-3 min-[420px]:flex-row min-[420px]:items-start min-[420px]:justify-between">
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                {group.category ? <CategoryIcon category={group.category} className="h-7 w-7" /> : null}
                                <span className="text-sm font-semibold text-slate-100">{tx.date}</span>
                                {txTags.map((tag) => (
                                  <span key={tag.id} className="rounded-full bg-slate-800 px-2 py-0.5 text-xs font-semibold text-slate-300">
                                    {tag.name}
                                  </span>
                                ))}
                              </div>
                              {tx.note ? <p className="mt-2 text-sm text-slate-400">{tx.note}</p> : null}
                            </div>
                            <div className="shrink-0 min-[420px]:text-right">
                              <p className={`text-base font-semibold min-[420px]:text-sm ${isIncome ? 'text-emerald-400' : 'text-rose-400'}`}>
                                {isIncome ? '+' : '-'}{money(tx.amount)}
                              </p>
                              <p className="mt-1 text-xs text-slate-500">{isIncome ? 'Entrée' : 'Sortie'}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <div className="print-bottom-grid space-y-5">
          <section className="section-card">
            <div className="mb-5">
              <h2 className="text-lg font-semibold text-slate-100">Catégories les plus dépensières</h2>
              <p className="mt-2 text-sm text-slate-400">Les postes qui pèsent le plus dans le mois.</p>
            </div>
            <StatsCategoryChart categories={selectedStats.topCategories} title="Top catégories" />
          </section>
        </div>
      </div>
      <BottomNavigation active="/stats" />
      <div className="page-inner mt-5">
        <AppFooter />
      </div>
    </main>
  );
}
